/** Structural subset of inbox rows; avoids importing the Pinia store (circular). */
export interface InboxNotificationDisplayRow {
  patient_first_name?: string | null
  patient_last_name?: string | null
  automation_event_type?: string | null
  data?: {
    title?: string
    body?: string
    task_uuid?: string
    opportunity_uuid?: string
    opportunity_uuids?: string[]
    program_uuid?: string
    source_program_uuid?: string
    target_program_uuid?: string
    merge_status?: 'completed' | 'completed_with_errors' | 'failed'
    retained_duplicate_opportunity_groups?: Array<{
      patient_case_id?: number
      opportunities?: Array<{
        id?: number
        uuid?: string | null
        status?: string | null
      }>
    }>
    audit_uuid?: string
    delivery_mode?: 'interrupt' | 'passive' | 'digest_eligible'

    /** Auto-discovery digest (`report_generated`): batch identifier and a full absolute results URL. */
    batch_id?: string
    download_url?: string

    /** Auto-discovery digest: absolute URL of the Insurance Inbox (VH-2583). */
    inbox_url?: string

    /** QuickBooks invoice upload finished processing (`invoice_upload_processed`, VH-3449). */
    invoice_upload_uuid?: string
    review_url?: string

    /** Terminal PAF failure payload (same shape as automation run `error_json`). */
    error_json?: unknown
  }
}

/** Inbox drawer tabs (excluding "all"). */
export type InboxNotificationCategory = 'action' | 'informational'

export type InboxCategoryTab = 'all' | InboxNotificationCategory

/** Minimal fields for badge math (keeps store from importing full `ProfileNotificationRow`). */
export interface InboxBellCountableRow {
  id: string
  read_at: string | null
  alertable?: boolean
  automation_event_type?: string | null
  taskable_type?: string | null
  dedupe_key?: string | null
  task_id?: number | null
  opportunity_id?: number | null
  data?: {
    taskable_type?: string
    task_uuid?: string
    opportunity_uuid?: string
    delivery_mode?: 'interrupt' | 'passive' | 'digest_eligible'
  }
}

/**
 * Unread passives (`alertable === false`) share one bell unit per merge group (matches server merge on
 * `automation_completed` + `taskable_type`). Alertable / default rows still count one per unread id.
 */
function inboxBellDedupeKey(row: InboxBellCountableRow): string {
  if (row.alertable !== false)
    return `id:${row.id}`

  if (row.automation_event_type === 'automation_completed') {
    const tt = row.taskable_type ?? row.data?.taskable_type ?? ''

    return `ac:${tt || row.id}`
  }

  if (row.dedupe_key)
    return `dk:${row.dedupe_key}`

  return `id:${row.id}`
}

/**
 * Bell / "All" tab unread total: one per alertable unread row id; one per passive merge group.
 */
export function inboxBellUnreadCount(rows: readonly InboxBellCountableRow[]): number {
  const seen = new Set<string>()
  let n = 0
  for (const row of rows) {
    if (row.read_at != null)
      continue
    const key = inboxBellDedupeKey(row)
    if (seen.has(key))
      continue
    seen.add(key)
    n += 1
  }

  return n
}

/**
 * Buckets rows for the drawer: interrupts / blocked-style vs passive completions and bulk-muted rows.
 * Keep in sync with `ProfileNotificationController` bulk delete `category` filter.
 */
export function inboxNotificationCategory(
  row: InboxNotificationDisplayRow & { alertable?: boolean },
): InboxNotificationCategory {
  const t = row.automation_event_type
  if (t === 'automation_completed')
    return 'informational'
  const mode = row.data?.delivery_mode
  if (mode === 'passive' || mode === 'digest_eligible')
    return 'informational'
  if (mode === 'interrupt')
    return 'action'
  if (row.alertable === false)
    return 'informational'

  return 'action'
}

export function inboxRowMatchesCategoryTab(
  row: InboxNotificationDisplayRow & { alertable?: boolean },
  tab: InboxCategoryTab,
): boolean {
  if (tab === 'all')
    return true

  return inboxNotificationCategory(row) === tab
}

function opportunitiesActionFromUuids(uuids: readonly string[]): { label: string; to: string } | null {
  if (!Array.isArray(uuids) || uuids.length === 0)
    return null

  const q = new URLSearchParams()

  // Use the existing opportunities page query contract (`opportunity_uuids` repeated keys).
  for (const uuid of uuids.slice(0, 20))
    q.append('opportunity_uuids', uuid)

  return { label: 'View opportunities', to: `/vector/opportunities?${q.toString()}` }
}

function retainedDuplicateOpportunityUuids(row: InboxNotificationDisplayRow): string[] {
  const retainedGroups = row.data?.retained_duplicate_opportunity_groups
  if (!Array.isArray(retainedGroups) || retainedGroups.length === 0)
    return []

  return retainedGroups
    .flatMap(group => Array.isArray(group?.opportunities) ? group.opportunities : [])
    .map(opportunity => opportunity?.uuid?.trim())
    .filter((uuid): uuid is string => Boolean(uuid))
}

function programPrimaryAction(row: InboxNotificationDisplayRow): { label: string; to: string } | null {
  const d = row.data
  if (!d?.program_uuid)
    return null

  if (d.audit_uuid && row.automation_event_type === 'permission_required')
    return { label: 'Review audit', to: `/vector/funders/program/${d.program_uuid}/audit/${d.audit_uuid}` }

  return { label: 'View program', to: `/vector/funders/program/${d.program_uuid}` }
}

/** VH-3449: the QuickBooks invoice upload is ready (or failed) — open that upload's review page. */
function invoiceUploadPrimaryAction(row: InboxNotificationDisplayRow): { label: string; to: string } | null {
  const uuid = row.data?.invoice_upload_uuid
  if (row.automation_event_type !== 'invoice_upload_processed' || !uuid)
    return null

  return {
    label: 'Review invoices',
    to: row.data?.review_url ?? `/vector/admin-tools/quickbooks-upload?upload=${uuid}`,
  }
}

export function inboxNotificationPrimaryAction(
  row: InboxNotificationDisplayRow,
): { label: string; to: string } | null {
  const d = row.data

  const completionAction = row.automation_event_type === 'automation_completed'
    ? opportunitiesActionFromUuids(d?.opportunity_uuids ?? [])
    : null

  if (completionAction)
    return completionAction

  // Patient replied to outreach — link directly to the opportunity Contact tab.
  if (row.automation_event_type === 'patient_outreach_replied' && d?.opportunity_uuid)
    return { label: 'View conversation', to: `/vector/opportunities/${d.opportunity_uuid}?tab=outreach` }

  const invoiceUploadAction = invoiceUploadPrimaryAction(row)
  if (invoiceUploadAction)
    return invoiceUploadAction

  const retainedAction = opportunitiesActionFromUuids(retainedDuplicateOpportunityUuids(row))
  if (retainedAction)
    return retainedAction

  if (d?.task_uuid)
    return { label: 'Review task', to: `/vector/tasks/${d.task_uuid}` }
  if (d?.target_program_uuid)
    return { label: 'View target program', to: `/vector/funders/program/${d.target_program_uuid}` }

  const programAction = programPrimaryAction(row)
  if (programAction)
    return programAction

  if (d?.opportunity_uuid)
    return { label: 'Review opportunity', to: `/vector/opportunities/${d.opportunity_uuid}` }

  // Auto-discovery digest (report_generated): `download_url` is a full absolute URL (server builds it
  // from `config('app.client_url')`), not a router-relative path. Safe to use as-is here — both consumers
  // of this return value (UserInboxDrawer's `goPrimary`, UserInboxToast's `onAction`) call
  // `window.open(link.to, '_blank', ...)`, never `router.push`, so an absolute URL works identically to
  // a relative one.
  // VH-2583: the digest now leads into the Insurance Inbox, where the discovered plans can
  // actually be resolved, rather than the batch report that had no action model. `inbox_url` is
  // only present on notifications written after the server change — anything already sitting
  // unread in a user's drawer still carries just `download_url`, so the fallback is load-bearing
  // rather than defensive.
  if (d?.batch_id && d?.inbox_url)
    return { label: 'Open Insurance Inbox', to: d.inbox_url }

  if (d?.batch_id && d?.download_url)
    return { label: 'View Results', to: d.download_url }

  return null
}

function formatNamePart(part: string | null | undefined): string {
  const t = (part ?? '').trim()
  if (!t)
    return ''
  const head = t[0] ?? ''
  const tail = t.slice(1)

  return head.toLocaleUpperCase() + tail.toLocaleLowerCase()
}

/** Returns a display name or null when both parts are empty (no patient / no names). */
export function formattedPatientName(row: InboxNotificationDisplayRow): string | null {
  const f = formatNamePart(row.patient_first_name)
  const l = formatNamePart(row.patient_last_name)
  const combined = [f, l].filter(Boolean).join(' ').trim()

  return combined || null
}

/** `Name - title` when a patient name exists; otherwise the stored title only. */
export function inboxNotificationTitle(row: InboxNotificationDisplayRow): string {
  const base = row.data?.title ?? 'Notification'
  const name = formattedPatientName(row)

  return name ? `${name} - ${base}` : base
}

/** Inbox / toast body: failures use `error_json.message` ?? server `body` (same `??` idea as task modals). */
export function inboxNotificationBody(row: InboxNotificationDisplayRow): string {
  const base = row.data?.body ?? ''
  if (row.automation_event_type !== 'automation_failed')
    return base

  const msg = (row.data?.error_json as { message?: string } | null | undefined)?.message

  return msg ?? base
}
